import React from 'react';
import { DefaultButton, Modal, PrimaryButton } from '@fluentui/react';
import { useCreateTenant } from '../hooks/useCreateTenant';
import styles from './CreateTenantModal.module.css';

interface PortfolioOption {
  portfolioId: string;
  friendlyName: string;
}

interface CommunityOption {
  communityId: string;
  friendlyName: string;
  displayName?: string;
  address?: string;
}

interface CreateTenantModalProps {
  isOpen: boolean;
  portfolios: PortfolioOption[];
  loadCommunities: (portfolioId: string) => void;
  communities: CommunityOption[];
  initialValues?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    portfolioId?: string;
    communityId?: string;
  };
  disablePortfolioSelect?: boolean;
  onDismiss: () => void;
  onCreated: (created: {
    emailNorm: string;
    firstName?: string;
    lastName?: string;
    portfolioId: string;
    communityId: string;
  }) => void;
  onViewTenant: (emailNorm: string) => void;
  onNotify: (type: 'success' | 'error', message: string) => void;
}

export const CreateTenantModal: React.FC<CreateTenantModalProps> = ({
  isOpen,
  portfolios,
  loadCommunities,
  communities,
  initialValues,
  disablePortfolioSelect = false,
  onDismiss,
  onCreated,
  onViewTenant,
  onNotify,
}) => {
  const { createTenant, loading, error, clearError } = useCreateTenant();
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [portfolioId, setPortfolioId] = React.useState('');
  const [communityId, setCommunityId] = React.useState('');
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const [createdEmail, setCreatedEmail] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFirstName(initialValues?.firstName || '');
    setLastName(initialValues?.lastName || '');
    setEmail(initialValues?.email || '');
    setPortfolioId(initialValues?.portfolioId || '');
    setCommunityId(initialValues?.communityId || '');
    setTouched({});
    setCreatedEmail(null);
    clearError();

    if (initialValues?.portfolioId) {
      loadCommunities(initialValues.portfolioId);
    }
  }, [isOpen, initialValues, loadCommunities, clearError]);

  React.useEffect(() => {
    if (portfolioId) {
      loadCommunities(portfolioId);
    }
  }, [portfolioId, loadCommunities]);

  const emailValid = /.+@.+\..+/.test(email.trim());
  const requiredValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    emailValid &&
    portfolioId.trim() &&
    communityId.trim();

  const errors = {
    firstName: firstName.trim() ? '' : 'First name is required.',
    lastName: lastName.trim() ? '' : 'Last name is required.',
    email: email.trim() ? (emailValid ? '' : 'Enter a valid email address.') : 'Email is required.',
    portfolioId: portfolioId ? '' : 'Portfolio is required.',
    communityId: communityId ? '' : 'Community is required.',
  };

  const showError = (field: keyof typeof errors) => touched[field] && errors[field];

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!requiredValid) {
      setTouched({
        firstName: true,
        lastName: true,
        email: true,
        portfolioId: true,
        communityId: true,
      });
      return;
    }

    try {
      const created = await createTenant({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        portfolioId,
        communityId,
      });

      setCreatedEmail(created.emailNorm);
      onCreated(created);
      onNotify('success', 'Tenant created');
    } catch (err: any) {
      onNotify('error', err?.message || 'Failed to create tenant');
    }
  };

  const handleClose = () => {
    onDismiss();
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleClose}
      isBlocking={false}
      containerClassName={styles.modal}
    >
      <div className={styles.header}>
        <h2>Create Tenant</h2>
        <p>Add a tenant and assign their community access.</p>
      </div>

      {createdEmail ? (
        <div className={styles.successState}>
          <div className={styles.successMessage}>Tenant created successfully.</div>
          <div className={styles.successActions}>
            <PrimaryButton
              text="View Tenant"
              onClick={() => onViewTenant(createdEmail)}
            />
            <DefaultButton text="Close" onClick={handleClose} />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>First Name *</label>
            <input
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, firstName: true }))}
              placeholder="e.g., Jane"
              required
            />
            {showError('firstName') && <span className={styles.errorText}>{errors.firstName}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Last Name *</label>
            <input
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, lastName: true }))}
              placeholder="e.g., Doe"
              required
            />
            {showError('lastName') && <span className={styles.errorText}>{errors.lastName}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Email *</label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
              placeholder="tenant@example.com"
              required
            />
            {showError('email') && <span className={styles.errorText}>{errors.email}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Portfolio *</label>
            <select
              value={portfolioId}
              onChange={(event) => {
                setPortfolioId(event.target.value);
                setCommunityId('');
                setTouched((prev) => ({ ...prev, portfolioId: true }));
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, portfolioId: true }))}
              disabled={disablePortfolioSelect}
              required
            >
              <option value="">-- Select a Portfolio --</option>
              {portfolios.map((portfolio) => (
                <option key={portfolio.portfolioId} value={portfolio.portfolioId}>
                  {portfolio.friendlyName}
                </option>
              ))}
            </select>
            {showError('portfolioId') && (
              <span className={styles.errorText}>{errors.portfolioId}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label>Community *</label>
            <select
              value={communityId}
              onChange={(event) => setCommunityId(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, communityId: true }))}
              disabled={!portfolioId}
              required
            >
              <option value="">-- Select a Community --</option>
              {communities.map((community) => (
                <option key={community.communityId} value={community.communityId}>
                  {community.friendlyName || community.displayName || community.address || community.communityId}
                </option>
              ))}
            </select>
            {showError('communityId') && (
              <span className={styles.errorText}>{errors.communityId}</span>
            )}
          </div>

          {error && <div className={styles.submitError}>{error}</div>}

          <div className={styles.actions}>
            <PrimaryButton type="submit" text={loading ? 'Creating...' : 'Create'} disabled={!requiredValid || loading} />
            <DefaultButton text="Cancel" onClick={handleClose} disabled={loading} />
          </div>
        </form>
      )}
    </Modal>
  );
};
